// Shared derivations for the profile hub and the screens it links to, so the
// row summaries on the hub and the headers on each screen can never disagree.

// Photo columns hold a storage key, not a URL — imageBase (the city's
// static_domain) is what turns one into something a browser can load.
export function photoUrl(imageBase, file) {
  if (!file) return null;
  if (/^(https?:)?\/\//.test(file) || file.startsWith('data:')) return file;
  return `${imageBase}/${file}`;
}

export function priceRange(menuItems) {
  const prices = (menuItems || [])
    .map((item) => parseFloat(item.price))
    .filter((p) => !Number.isNaN(p));
  if (prices.length === 0) return null;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? `$${Math.round(min)}` : `$${Math.round(min)}–$${Math.round(max)}`;
}

// Groups in the order the public menu renders them: vendor-named categories
// first in their own order, then anything still on the old enum heading.
export function groupedMenu(truck) {
  const items = truck.menu_items || [];
  const categories = truck.menu_categories || [];
  const named = categories.map((category) => ({
    id: category.id,
    name: category.name,
    hidden: category.hidden,
    items: items.filter((item) => item.menu_category_id === category.id),
  }));

  const unfiled = items.filter((item) => !item.menu_category_id);
  const legacy = [];
  unfiled.forEach((item) => {
    const name = item.group_name || 'Menu';
    const found = legacy.find((group) => group.name === name);
    if (found) found.items.push(item);
    else legacy.push({ id: null, name, hidden: false, items: [item] });
  });

  return [...named, ...legacy];
}

export function photoCounts(photos) {
  const all = photos || [];
  return {
    total: all.length,
    logo: all.filter((p) => p.category === 'logo').length,
    truck: all.filter((p) => p.category === 'truck').length,
    // "menu" is what the column has always stored for the design's Food group.
    food: all.filter((p) => p.category === 'menu').length,
  };
}

export function initials(name) {
  return (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
}

export function money(value) {
  const n = parseFloat(value);
  return Number.isNaN(n) ? '' : `$${n.toFixed(2)}`;
}

// The one thing most worth doing next, or null when the profile is in shape.
// Ordered by what actually moves invites: a menu first, then food photos, then
// the story hosts read.
export function nextBestAction(truck, basePath) {
  const items = truck.menu_items || [];
  const counts = photoCounts(truck.photos);

  if (items.length === 0) {
    return {
      label: 'Add your menu',
      note: 'Customers pick trucks by menu, and people searching for what you sell won’t find you without one.',
      to: `${basePath}/profile/menu`,
    };
  }
  if (counts.food < 5) {
    const missing = 5 - counts.food;
    return {
      label: `Add ${missing} more food photo${missing === 1 ? '' : 's'}`,
      note: 'Add food photos so customers can see what you serve.',
      to: `${basePath}/profile/photos`,
    };
  }
  if (!truck.description) {
    return {
      label: 'Write your story',
      note: 'Customers read this before they invite you. Two or three sentences is plenty.',
      to: `${basePath}/profile/story`,
    };
  }
  if (counts.logo === 0) {
    return {
      label: 'Add your logo',
      note: 'Your logo is your map pin and the avatar on every invite.',
      to: `${basePath}/profile/photos`,
    };
  }
  return null;
}
